import { get, keyBy } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { Small, MediumUp } from 'components/MediaQuery';
import { Sub, SubBold } from 'components/Sub';
import { UPSELL_RECOMMENDATION_TYPES } from 'constants/cartUpsells';
import CartUpsell from 'models/CartUpsell';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';
import Rudderstack from 'utils/Rudderstack';

import AddToCart from './AddToCart';
import ColorSwatchCarousel from './ColorSwatchCarousel';
import { ONE_SIZE, EVENTS } from './constants';
import Image from './Image';
import SizeSelector from './SizeSelector';

const Upsell = class extends React.PureComponent {
  static propTypes = {
    productName: PropTypes.string.isRequired,
    productType: PropTypes.string.isRequired,
    recommendedItems: PropTypes.arrayOf(PropTypes.instanceOf(CartUpsell)).isRequired,
    recommendationPairingName: PropTypes.string,
    addToCart: PropTypes.func,
    updateRecommendation: PropTypes.func,
    trackEvent: PropTypes.func,
    uiEnhancementsEngagement: PropTypes.shape({
      dyDecisionId: PropTypes.string,
      dyVariationId: PropTypes.number,
    }),
  };

  static defaultProps = {
    recommendationPairingName: '',
    addToCart: () => {},
    updateRecommendation: () => {},
    trackEvent: () => {},
    uiEnhancementsEngagement: {},
  };

  constructor(props) {
    super(props);

    this.state = {
      activeColorwayHandle: get(this.props.recommendedItems[0], 'handle'),
      slideIndex: 0,
      isExpanded: false,
      selectedSize: null,
      isAtcDisabled: true,
      label: this.selectorName,
    };
  }

  componentDidMount() {
    this.props.trackEvent({
      eventAction: EVENTS.UPSELL_VIEW,
      eventLabel: this.props.recommendationPairingName,
    });
  }

  // Need this to update child from parent state change, otherwise initial state is not set
  /* eslint-disable */
  componentDidUpdate(prevProps) {
    if (this.props.productName !== prevProps.productName) {
      this.props.trackEvent({
        eventAction: EVENTS.UPSELL_VIEW,
        eventLabel: this.props.recommendationPairingName,
      });
      this.setState({ activeColorwayHandle: get(this.props.recommendedItems[0], 'handle') });
    }
  }
  /* eslint-enable */

  get mappedRecommendations() {
    return keyBy(this.props.recommendedItems, 'handle');
  }

  get productName() {
    return get(this.mappedRecommendations[this.state.activeColorwayHandle], 'productName');
  }

  get image() {
    return get(this.mappedRecommendations[this.state.activeColorwayHandle], 'image');
  }

  get price() {
    return get(this.mappedRecommendations[this.state.activeColorwayHandle], 'price');
  }

  get sizes() {
    return get(this.mappedRecommendations[this.state.activeColorwayHandle], 'mappedSizes');
  }

  get isUnisize() {
    return get(this.mappedRecommendations[this.state.activeColorwayHandle], 'isUnisize');
  }

  get linkUrl() {
    return get(this.mappedRecommendations[this.state.activeColorwayHandle], 'linkUrl');
  }

  get selectorName() {
    return this.props.productType === UPSELL_RECOMMENDATION_TYPES.GIFT_CARDS
      ? I18n.localize('upsells.selectAmount')
      : I18n.localize('upsells.selectSize');
  }

  getRudderstackProperties = selectedReccomendation => {
    return {
      category: get(this.mappedRecommendations[selectedReccomendation], ['_data', 'productType']),
      name: get(this.mappedRecommendations[selectedReccomendation], ['_data', 'productName']),
      colorName: get(this.mappedRecommendations[selectedReccomendation], 'colorName'),
    };
  };

  handleSwatchSelect = e => {
    this.props.trackEvent({
      eventAction: EVENTS.UPSELL,
      eventLabel: EVENTS.COLOR_SWATCH_CLICK,
      dyDecisionId: this.props.uiEnhancementsEngagement?.dyDecisionId,
      dyVariationId: this.props.uiEnhancementsEngagement?.dyVariationId,
    });

    this.setState({ activeColorwayHandle: e }, () => {
      if (this.state.selectedSize === null) {
        return;
      }
      if (get(this.sizes[this.state.selectedSize], 'isAvailable')) {
        this.setState({ isAtcDisabled: false, label: I18n.localize('upsells.addToCart') });
      } else {
        this.setState({ isAtcDisabled: true, label: I18n.localize('upsells.soldOut') });
      }
    });

    Rudderstack.cartUpsellSwatchClicked(this.getRudderstackProperties(e));
  };

  handleExpand = () => {
    this.props.trackEvent({
      eventAction: EVENTS.UPSELL,
      eventLabel: EVENTS.OPEN_SIZE_SELECTOR,
      dyDecisionId: this.props.uiEnhancementsEngagement?.dyDecisionId,
      dyVariationId: this.props.uiEnhancementsEngagement?.dyVariationId,
    });
    this.setState(prevState => ({
      isExpanded: !prevState.isExpanded,
    }));
    this.setState({
      selectedSize: null,
      label: this.selectorName,
      isAtcDisabled: true,
    });
  };

  handleSlideDecrement = () => {
    this.setState(prevState => ({
      slideIndex: prevState.slideIndex - 1,
    }));
  };

  handleSlideIncrement = () => {
    this.setState(prevState => ({
      slideIndex: prevState.slideIndex + 1,
    }));
  };

  handleSizeSelect = (size, isAvailable) => {
    this.setState({ selectedSize: size });
    if (isAvailable) {
      this.setState({ isAtcDisabled: false, label: I18n.localize('upsells.addToCart') });
    } else {
      this.setState({ isAtcDisabled: true, label: I18n.localize('upsells.soldOut') });
    }

    Rudderstack.cartUpsellSizeSelectorClicked({
      ...this.getRudderstackProperties(this.state.activeColorwayHandle),
      sku: get(this.sizes[size], ['_data', 'sku']),
      isAvailable,
    });
  };

  handleAddToCart = () => {
    this.props.trackEvent({
      eventAction: EVENTS.UPSELL_ATC,
      eventLabel: this.props.recommendationPairingName,
      dyDecisionId: this.props.uiEnhancementsEngagement?.dyDecisionId,
      dyVariationId: this.props.uiEnhancementsEngagement?.dyVariationId,
    });
    this.props.addToCart(get(this.sizes[this.state.selectedSize], 'variantId'));
    this.props.updateRecommendation();

    this.setState({
      isExpanded: false,
      selectedSize: null,
      label: this.selectorName,
      isAtcDisabled: true,
      slideIndex: 0,
    });
  };

  handleUnisizeAddToCart = () => {
    this.props.trackEvent({
      eventAction: EVENTS.UPSELL_ATC,
      eventLabel: this.props.recommendationPairingName,
      dyDecisionId: this.props.uiEnhancementsEngagement?.dyDecisionId,
      dyVariationId: this.props.uiEnhancementsEngagement?.dyVariationId,
    });
    this.setState({ slideIndex: 0 });
    return this.props.addToCart(get(this.sizes[ONE_SIZE], 'variantId'));
  };

  render() {
    const { recommendedItems, productType, trackEvent } = this.props;

    return (
      <div className="Upsell">
        <div className="Upsell__top-wrapper">
          <Image src={get(this.image, 'src')} alt={get(this.image, 'alt')} href={this.linkUrl} />
          <div className="Upsell__description">
            <div className="Upsell__top-section">
              <SubBold className="Upsell__product-name">{this.productName}</SubBold>
              {productType !== UPSELL_RECOMMENDATION_TYPES.GIFT_CARDS && (
                <Sub className="Upsell__price">{this.price}</Sub>
              )}
            </div>
            {productType !== UPSELL_RECOMMENDATION_TYPES.GIFT_CARDS && (
              <div>
                <Small>
                  <ColorSwatchCarousel
                    recommendedItems={recommendedItems}
                    handleSwatchSelect={this.handleSwatchSelect}
                    activeColorwayHandle={this.state.activeColorwayHandle}
                    slideIndex={this.state.slideIndex}
                    isMobile
                    handleSlideIncrement={this.handleSlideIncrement}
                    handleSlideDecrement={this.handleSlideDecrement}
                  />
                </Small>
                <MediumUp>
                  <ColorSwatchCarousel
                    recommendedItems={recommendedItems}
                    handleSwatchSelect={this.handleSwatchSelect}
                    activeColorwayHandle={this.state.activeColorwayHandle}
                    slideIndex={this.state.slideIndex}
                    isMobile={false}
                    handleSlideIncrement={this.handleSlideIncrement}
                    handleSlideDecrement={this.handleSlideDecrement}
                  />
                </MediumUp>
              </div>
            )}
          </div>
        </div>
        {this.isUnisize ? (
          <AddToCart
            addToCart={this.handleUnisizeAddToCart}
            updateRecommendation={this.props.updateRecommendation}
          />
        ) : (
          <SizeSelector
            selectorName={this.selectorName}
            sizes={this.sizes}
            productType={productType}
            isExpanded={this.state.isExpanded}
            selectedSize={this.state.selectedSize}
            label={this.state.label}
            isAtcDisabled={this.state.isAtcDisabled}
            handleAddToCart={this.handleAddToCart}
            handleExpand={this.handleExpand}
            handleSizeSelect={this.handleSizeSelect}
            trackEvent={trackEvent}
          />
        )}
        <style jsx>
          {`
            .Upsell {
              background-color: ${Theme.get('colors-grey')};
            }

            .Upsell__top-wrapper {
              display: flex;
              padding: ${Theme.get('spacing') * 3}px;
              border-bottom: 1px solid ${Theme.get('colors-black-20')};
            }

            .Upsell__description {
              display: flex;
              flex-direction: column;
              width: 80%;
            }

            .Upsell__top-section {
              display: flex;
            }

            .Upsell__bottom-section {
              height: 100%;
            }
          `}
        </style>
        <style jsx global>
          {`
            .Sub.Upsell__product-name {
              flex-grow: 1;
            }
          `}
        </style>
      </div>
    );
  }
};

export default Upsell;
